export type DateInput = string | number | Date | null | undefined;

const BEIJING_TIME_ZONE = 'Asia/Shanghai';

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatInBeijing(
  value: DateInput,
  options: Intl.DateTimeFormatOptions,
  fallback = '-',
  locale = 'zh-CN'
): string {
  const date = toDate(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat(locale, {
    timeZone: BEIJING_TIME_ZONE,
    ...options,
  }).format(date);
}

function formatWithParts(value: DateInput): Intl.DateTimeFormatPart[] | null {
  const date = toDate(value);
  if (!date) return null;

  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: BEIJING_TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
}

function getPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((part) => part.type === type)?.value ?? '';
}

export function formatBeijingDateKey(value: DateInput = new Date()): string {
  const parts = formatWithParts(value);
  if (!parts) return '';
  return `${getPart(parts, 'year')}-${getPart(parts, 'month')}-${getPart(parts, 'day')}`;
}

export function addDaysToBeijingDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  if (![year, month, day].every(Number.isFinite)) return dateKey;
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  const nextYear = String(date.getUTCFullYear());
  const nextMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
  const nextDay = String(date.getUTCDate()).padStart(2, '0');
  return `${nextYear}-${nextMonth}-${nextDay}`;
}

export function formatBeijingDate(value: DateInput, fallback = '-'): string {
  const parts = formatWithParts(value);
  if (!parts) return fallback;
  return `${getPart(parts, 'year')}/${getPart(parts, 'month')}/${getPart(parts, 'day')}`;
}

export function formatBeijingDateTime(value: DateInput, fallback = '-'): string {
  const parts = formatWithParts(value);
  if (!parts) return fallback;
  return `${getPart(parts, 'year')}/${getPart(parts, 'month')}/${getPart(parts, 'day')} ${getPart(parts, 'hour')}:${getPart(parts, 'minute')}:${getPart(parts, 'second')}`;
}

export function formatBeijingMonthDay(value: DateInput, fallback = '-'): string {
  const parts = formatWithParts(value);
  if (!parts) return fallback;
  return `${getPart(parts, 'month')}/${getPart(parts, 'day')}`;
}
